import SpatialAdjustEntityType = ToroEnums.SpatialAdjustEntityType;

import { catchError, concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { defer, EMPTY, firstValueFrom, Observable, of, retry, Subject, Subscription, timeout, TimeoutError, timer } from 'rxjs';
import { AuthManagerService } from '../auth/auth-manager.service';
import { BroadcastService } from '../../common/services/broadcast.service';
import { CorrelationService } from '../../common/services/correlation.service';
import { duration } from 'moment';
import { GetCourseReportStation } from './models/mqtt-get-course-report/get-course-report-station.model';
import { GetCoursesCourse } from './models/mqtt-get-courses/get-courses-course.model';
import { GetGeoTopoArea } from './models/mqtt-get-geo-topo/get-geo-topo-area.model';
import { GetProgramTopoArea } from './models/mqtt-get-program-topo/get-program-topo-area.model';
import { GetStationsStation } from './models/mqtt-get-stations/get-stations-station.model';
import { LoggerService } from '../../common/services/logger.service';
import { LynxCloudCourseItem } from '../../core/dashboard/widgets/lynx/widget-lynx-cloud/models/lynx-cloud-course-item.model';
import { LynxPercentAdjust } from './models/mqtt-percent-adjust/lynx-percent-adjust.model';
import { PingResponse } from './models/mqtt-ping/ping-response.model';
import { SaLynxStationAdjustItem } from './models/sa-station-adjust-item.model';
import { StationAdjustDict } from './models/station-adjust-dict.model';
import { StationAdjustInfo } from './models/station-adjust-info.model';
import { StationStatuses } from './models/station-status/station-statuses.model';
import { StationStatusItem } from './models/station-status/station-status-item.model';
import { StationStatusStation } from './models/station-status/station-status-station.model';
import { ToroEnums } from '../../common/enumerations/toro.enums';
import { TranslateService } from '@ngx-translate/core';

export abstract class CloudServiceBase {
    // Subjects
    public stationAdjustListUpdated = new Subject<SaLynxStationAdjustItem[]>();
    public courseListRetrieved = new Subject<GetCoursesCourse[]>();
    public stationsRetrieved = new Subject<GetStationsStation[]>();
    public programTopoRetrieved = new Subject<GetProgramTopoArea[]>();
    public percentAdjustmentsRetrieved = new Subject<LynxPercentAdjust[]>();
    public geoTopographyRetrieved = new Subject<GetGeoTopoArea[]>();
    public courseReportRetrieved = new Subject<GetCourseReportStation[]>();
    public errorRetrievingLynxData = new Subject<string>();
    public lynxTimeoutError = new Subject<Error>();
    public pingResponseReceived = new Subject<PingResponse>();

    // Constants
    protected spacialAdjustDataReady = new Subject<StationAdjustDict>();

    private readonly timeoutInMs: number = 60000;
    private readonly maxRetries = 3;
    private readonly retryDelayInMs = 5000;

    // Member Vars
    protected installationIds: string[];
    protected siteId: number;
    protected spatialAdjustCourseItem: LynxCloudCourseItem;
    protected stationAdjustDict: StationAdjustDict = {};

    protected _stationAdjustList: SaLynxStationAdjustItem[] = [];

    private _lynxStations: GetStationsStation[] = [];
    public set lynxStations(value: GetStationsStation[]) {
        this._lynxStations = value;
    }

    public get lynxStations(): GetStationsStation[] {
        return this._lynxStations;
    }

    private _lynxPrograms: GetProgramTopoArea[] = [];
    public set lynxPrograms(value: GetProgramTopoArea[]) {
        this._lynxPrograms = value;
    }

    public get lynxPrograms(): GetProgramTopoArea[] {
        return this._lynxPrograms;
    }

    private _lynxPercentAdjustments: LynxPercentAdjust[] = [];
    public set lynxPercentAdjustments(value: LynxPercentAdjust[]) {
        this._lynxPercentAdjustments = value;
    }

    public get lynxPercentAdjustments(): LynxPercentAdjust[] {
        return this._lynxPercentAdjustments;
    }

    private _isInitialized = false;
    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    protected set isInitialized(value: boolean) {
        this._isInitialized = value;
    }

    private isRetrievingStationStatuses = false;
    private isRetrievingSpatialAdjustData = false;
    private consecutivePingTimeouts = 0;

    // =========================================================================================================================================================
    // Ctor
    // =========================================================================================================================================================

    constructor(protected authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                protected correlationService: CorrelationService,
                protected logger: LoggerService,
                protected translate: TranslateService,
    ) {
        this.setSiteAndInstallationIds();
    }

    private setSiteAndInstallationIds() {
        if (this.authManager?.dashAuthenticatedUser == null) {
            setTimeout(() => this.setSiteAndInstallationIds(), 100);
            return;
        }

        // Get the LynxCloud Installation Id from the Dash User object. This will come from NSN.
        this.installationIds = this.authManager.dashAuthenticatedUser.lynxCloudInstallationIds;
        this.siteId = this.authManager.dashAuthenticatedUser.siteId;
    }

    // =========================================================================================================================================================
    // Common Methods called from derived classes
    // =========================================================================================================================================================

    // Abstract classes to be overridden in derived classes
    protected abstract requestPingLynx(courseItem: LynxCloudCourseItem): void;

    protected abstract requestCoursesInternal(): void;

    protected abstract requestStations(courseItem: LynxCloudCourseItem): void;

    protected abstract requestProgramTopo(courseItem: LynxCloudCourseItem): void;

    protected abstract requestPercentAdjustments(courseItem: LynxCloudCourseItem): void;

    protected abstract requestGeoTopography(courseItem: LynxCloudCourseItem): void;

    protected abstract requestCourseReport(courseItem: LynxCloudCourseItem): void;

    // Called via LynxProxyService
    public get stationAdjustList(): SaLynxStationAdjustItem[] {
        return this._stationAdjustList;
    }

    // Called via LynxProxyService - Used by Lynx Cloud Widget
    public getStationStatuses(courseItem: LynxCloudCourseItem): Observable<StationStatuses> {
        if (this.isRetrievingStationStatuses) {
            this.logger.warn('Lynx: Already retrieving station statuses. Previous attempt slow.')
            return of(null);
        }

        this.isRetrievingStationStatuses = true;

        return defer(() => {
            if (courseItem != null) this.spatialAdjustCourseItem = courseItem;
            if (this.spatialAdjustCourseItem == null) {
                this.logger.warn('No Lynx Course Selected. Aborting retrieval of Lynx data.')
                throw new Error('No course selected');
            }

            const c = this.spatialAdjustCourseItem;

            let geoTopoAreas: GetGeoTopoArea[];
            let courseReportStations: GetCourseReportStation[];
            let stations: GetStationsStation[];

            // 1) GeoTopoAreas
            const geoTop$ = this.requestAndWaitStep(() => this.requestGeoTopography(c), this.geoTopographyRetrieved,
                this.timeoutInMs,                            // timeout per attempt (ms)
                this.maxRetries,                             // attempts
                this.retryDelayInMs                          // delay between attempts (ms)
            ).pipe(tap((response: GetGeoTopoArea[]) => {
                geoTopoAreas = response;
            }))

            // 2) CourseReport
            const courseReport$ = this.requestAndWaitStep(() => this.requestCourseReport(c), this.courseReportRetrieved,
                this.timeoutInMs,                            // timeout per attempt (ms)
                this.maxRetries,                             // attempts
                this.retryDelayInMs                          // delay between attempts (ms)
            ).pipe(tap((response: GetCourseReportStation[]) => {
                courseReportStations = response;
            }))

            // 3) Stations
            const stations$ = this.requestAndWaitStep(() => this.requestStations(c), this.stationsRetrieved,
                this.timeoutInMs,                            // timeout per attempt (ms)
                this.maxRetries,                             // attempts
                this.retryDelayInMs                          // delay between attempts (ms)
            ).pipe(tap((response: GetStationsStation[]) => {
                stations = response;
            }))

            let start: number;
            let elapsedMs: number;

            start = performance.now();
            this.logger.info('Lynx Cloud Widget: Requesting Geo Topo (step: 1 of 3)')
            return geoTop$
                .pipe(
                    concatMap(() => {
                        elapsedMs = performance.now() - start;
                        this.logger.info('Lynx Cloud Widget: Geo Topo retrieval complete', { elapsedMs: elapsedMs.toFixed(0) });

                        start = performance.now();
                        this.logger.info('Lynx Cloud Widget: Requesting Course Report (step: 2 of 3)')
                        return courseReport$
                    }),
                    concatMap(() => {
                        elapsedMs = performance.now() - start;
                        this.logger.info('Lynx Cloud Widget: Course Report retrieval complete', { elapsedMs: elapsedMs.toFixed(0) });

                        start = performance.now();
                        this.logger.info('Lynx Cloud Widget: Requesting Stations (step: 3 of 3)')
                        return stations$;
                    }),
                    map(() => {
                        elapsedMs = performance.now() - start;
                        this.logger.info('Lynx Cloud Widget: Stations retrieval complete', { elapsedMs: elapsedMs.toFixed(0) });

                        const areas = geoTopoAreas as GetGeoTopoArea[];

                        const { running, ranUnder } = areas.reduce((acc, area) => {
                            area.holes.forEach(hole => {
                                hole.stations.forEach(stationId => {
                                    const reportStation = courseReportStations.find(r => r.stationId === stationId);
                                    if (reportStation) {
                                        if (reportStation.isRunning) {
                                            const sta = new StationStatusStation({ ...reportStation, name: stations.find(s => s.id === stationId).name });
                                            acc.running.push(new StationStatusItem(area.name, hole.name, sta));
                                        }
                                        if (reportStation.totalDelta < duration('-00:00:01')) {
                                            const sta = new StationStatusStation({ ...reportStation, name: stations.find(s => s.id === stationId).name });
                                            acc.ranUnder.push(new StationStatusItem(area.name, hole.name, sta));
                                        }
                                    }
                                });
                            });
                            return acc;
                        }, { running: [], ranUnder: [] });

                        return new StationStatuses(running, ranUnder);
                    })
                );

        }).pipe(
            finalize(() => {
                this.isRetrievingStationStatuses = false;
            }),
            catchError(err => {
                this.logger.error('Error requesting Lynx data for widget.', err);
                throw err;
            })
        );
    }

    public pingLynx(courseItem: LynxCloudCourseItem): Observable<PingResponse> {
        if (this.isRetrievingSpatialAdjustData) { return of(null); }

        this.requestPingLynx(courseItem);

        return new Observable<PingResponse>(observer => {
            let response: PingResponse;
            const subs = new Subscription();

            const done = () => {
                observer.next(response);
                observer.complete();
                subs.unsubscribe(); // stop any pending inner streams to avoid late timeouts
            };

            const sub = this.pingResponseReceived.pipe(
                timeout(5000),
                take(1),
                catchError(err => {
                    if (err instanceof TimeoutError) {
                        this.consecutivePingTimeouts++;

                        if (this.consecutivePingTimeouts >= 2) {
                            this.lynxTimeoutError.next(err);
                        }
                    }

                    this.logger.error('Error pinging Lynx', err);
                    return EMPTY;
                }))
                .subscribe({
                    next: (resp) => {
                        this.consecutivePingTimeouts = 0; // successful ping resets count

                        response = resp;
                        done();
                    },
                    error: (err) => {
                        this.logger.error('Error in ping response stream', err);
                        done();
                    },
                });

            subs.add(sub);

            // Teardown
            return () => subs.unsubscribe();
        });
    }

    // Called via LynxProxyService
    public getCourseList(): Observable<GetCoursesCourse[]> {
        // If we don't have any Lynx Cloud Installation Ids, bale out.
        if (this.installationIds?.length < 1 || this.installationIds[0] == '') return of(null);

        this.requestCoursesInternal();

        return new Observable<GetCoursesCourse[]>(observer => {
            if (!this.installationIds?.length) {
                observer.next([]);
                observer.complete();
                return;
            }

            let responseCount = 0;
            const courses: GetCoursesCourse[] = [];
            const retrievedInstallationIds: string[] = [];
            const subs = new Subscription();

            const done = () => {
                observer.next(courses);
                observer.complete();
                subs.unsubscribe(); // stop any pending inner streams to avoid late timeouts
            };

            this.installationIds.forEach(id => {
                const sub = this.courseListRetrieved.pipe(
                    timeout(5000),      // Required to handle 'bogus' installation IDs that will not return valid course information.
                    catchError(err => {
                        this.logger.error('Error retrieving Lynx courses for installation ID', {installationId: id, err});

                        // count this ID as a response (timeout or other error)
                        responseCount++;
                        if (responseCount === this.installationIds.length) done();
                        return EMPTY; // swallow the error so it doesn't surface
                    }),
                )
                    .subscribe({
                        next: (result) => {
                            if (!result?.length) return;

                            // dedup check
                            if (!retrievedInstallationIds.includes(result[0].installationId)) {
                                retrievedInstallationIds.push(result[0].installationId);
                                courses.push(...result);
                                responseCount++;
                                if (responseCount === this.installationIds.length) done();
                            }
                        },
                        error: (err) => {
                            this.logger.error('Error retrieving Lynx Course List.', err);

                            // Shouldn’t happen because catchError -> EMPTY, but safe to handle
                            responseCount++;
                            if (responseCount === this.installationIds.length) done();
                        }
                    });

                subs.add(sub);
            });

            // Teardown
            return () => subs.unsubscribe();
        });
    }

    // Called via LynxProxyService
    public async requestSpatialAdjustData(courseItem: LynxCloudCourseItem | null = null): Promise<void> {
        if (courseItem) this.spatialAdjustCourseItem = courseItem;
        if (!this.spatialAdjustCourseItem) return;

        this.isRetrievingSpatialAdjustData = true;

        const c = this.spatialAdjustCourseItem;

        // reset local caches
        const totalSteps: number = 3;
        let step: number = 1;
        this.lynxStations = [];
        this.lynxPrograms = [];
        this.lynxPercentAdjustments = [];

        const correlationId = this.correlationService.createCorrelationId();

        try {
            let start: number;
            let elapsedMs: number;

            this.logger.info('Retrieving Lynx data', {correlationId, installationId: courseItem.installationId, courseId: courseItem.courseId});

            // 1) Stations
            this.broadcastService.saFooterStatus.next(this.translate.instant('SPATIAL_ADJUST.STATUS_RETRIEVING_LYNX_DATA_STEP_X_OF_Y', {x: step, y: totalSteps}));
            this.logger.info('Lynx: Requesting Stations (step: 1 of 3)', {correlationId, installationId: courseItem.installationId, courseId: courseItem.courseId});
            start = performance.now();

            this.lynxStations = await firstValueFrom(
                this.requestAndWaitStep(
                    () => this.requestStations(c),        // triggers API → MQTT → SignalR
                    this.stationsRetrieved,               // Subject/Observable emitting stations
                    this.timeoutInMs,                          // timeout per attempt (ms)
                    this.maxRetries,                           // attempts
                    this.retryDelayInMs                        // delay between attempts (ms)
                )
            );
            elapsedMs = performance.now() - start;
            this.logger.info('Lynx: Station retrieval complete', { count: this.lynxStations.length, elapsedMs: elapsedMs.toFixed(0), correlationId });

            // 2) Program topo
            this.broadcastService.saFooterStatus.next(this.translate.instant('SPATIAL_ADJUST.STATUS_RETRIEVING_LYNX_DATA_STEP_X_OF_Y', {x: ++step, y: totalSteps}));
            this.logger.info('Lynx: Requesting Program Topo (step: 2 of 3)', { correlationId, installationId: courseItem.installationId, courseId: courseItem.courseId });
            start = performance.now();

            this.lynxPrograms = await firstValueFrom(
                this.requestAndWaitStep(() => this.requestProgramTopo(c), this.programTopoRetrieved,
                    this.timeoutInMs,                            // timeout per attempt (ms)
                    this.maxRetries,                             // attempts
                    this.retryDelayInMs                          // delay between attempts (ms)
                )
            );
            elapsedMs = performance.now() - start;
            this.logger.info('Lynx: Program Topo retrieval complete', { count: this.lynxPrograms.length, elapsedMs: elapsedMs.toFixed(0), correlationId });

            // 3) Percent adjustments
            this.broadcastService.saFooterStatus.next(this.translate.instant('SPATIAL_ADJUST.STATUS_RETRIEVING_LYNX_DATA_STEP_X_OF_Y', {x: ++step, y: totalSteps}));
            this.logger.info('Lynx: Requesting Percent Adjustments (step: 3 of 3)', { correlationId, installationId: courseItem.installationId, courseId: courseItem.courseId });
            start = performance.now();

            this.lynxPercentAdjustments = await firstValueFrom(
                this.requestAndWaitStep(() => this.requestPercentAdjustments(c), this.percentAdjustmentsRetrieved,
                    this.timeoutInMs,                            // timeout per attempt (ms)
                    this.maxRetries,                             // attempts
                    this.retryDelayInMs                          // delay between attempts (ms)
                )
            );
            elapsedMs = performance.now() - start;
            this.logger.info('Lynx: Percent Adjustments retrieval complete', { count: this.lynxPercentAdjustments.length, elapsedMs: elapsedMs.toFixed(0), correlationId });

            // Reset flag to allow Ping requests
            this.isRetrievingSpatialAdjustData = false;

            // success: all three completed
            this.checkSpatialAdjustDataStatus(correlationId);
        }
        catch (err) {
            // Reset flag to allow Ping requests
            this.isRetrievingSpatialAdjustData = false;

            // a step exhausted its retries (timeout or other error)
            this.logger.error('Error requesting Lynx data', err, correlationId);

            if (err instanceof TimeoutError) {
                if (this.consecutivePingTimeouts >= 2) {
                    this.lynxTimeoutError.next(err);
                }
            }

            this.errorRetrievingLynxData.next('Error requesting Lynx data.');
        }
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private checkSpatialAdjustDataStatus(correlationId: string) {
        // All data has been retrieved from Lynx. Verify the data is valid. If not, throw error.
        if (this.lynxStations.length < 1 || this.lynxPrograms.length < 1 || this.lynxPercentAdjustments.length < 1) {
            this.logger.error('Incomplete or invalid data was received from Lynx.', { stations: this.lynxStations, programs: this.lynxPrograms, adjustments: this.lynxPercentAdjustments, correlationId });
            this.errorRetrievingLynxData.next('Incomplete or invalid data was received from Lynx.');
            return;
        }

        this.logger.info('Lynx: All Lynx data retrieved successfully', correlationId);

        this.createStationAdjustDictionary();
    }

    private createStationAdjustDictionary() {
        if (this.lynxStations.length < 1 || this.lynxPrograms.length < 1 || this.lynxPercentAdjustments.length < 1) { return; }

        const isLynxCloudUser = !this.authManager.dashAuthenticatedUser?.isLynxDriveUser;
        this.stationAdjustDict = {};

        this.lynxStations.forEach(sta => {
            const programs = this.lynxPrograms.filter(p => p.holes.some(h => h.stations.some(s => s.stationId == sta.id)));

            this.stationAdjustDict[sta.name] = [];

            programs.forEach(p => {
                const station = p.holes.flatMap(h => h.stations).find(s => s.stationId === sta.id);

                // NOTE: entityId is an object and will be null for Lynx Cloud users.
                const adjustment = isLynxCloudUser
                    ? this.lynxPercentAdjustments.find(a => a.id === station.id)
                    : this.lynxPercentAdjustments.find(a => a.entityId.type == SpatialAdjustEntityType.ProgramStation && a.id === station.id);

                this.stationAdjustDict[sta.name].push(new StationAdjustInfo({
                    programId: p.id,
                    programName: p.name,
                    stationId: sta.id,
                    adjustment: adjustment,
                }));
            });
        });

        this.spacialAdjustDataReady.next(this.stationAdjustDict);

        this.createStationAdjustList(this.stationAdjustDict);
    }

    private createStationAdjustList(stationAdjustDict: StationAdjustDict) {
        this._stationAdjustList = Object.entries(stationAdjustDict)
            .map(([key, data]) => {
                if (data == null || data.length < 1) return;

                return new SaLynxStationAdjustItem({
                    stationName: key,
                    percentAdjustPercent: data[0].adjustment.basePercentAdjust,
                    stationAdjustId: data[0].adjustment.id
                });
            });

        // TODO - Update SA Demo Data: Grab areaData, suggestedPctAdjustments, SaSettings;
        // try {
        //     const x = JSON.stringify(this._stationAdjustList);
        //     this.logger.debug(x);
        // } catch (error) {
        //     this.logger.debug(error);
        // }

        this.stationAdjustListUpdated.next(this._stationAdjustList);
    }

    private requestAndWaitStep<T>(request: () => void, event$: Observable<T>, timeoutMs = 5000, attempts = 3, delayMs = 2000): Observable<T> {
        const oneAttempt$ = defer(() => {
            // Subscribe first so we can't miss a fast response
            const wait$ = event$.pipe(take(1), timeout(timeoutMs));
            request();
            return wait$;
        });

        return oneAttempt$.pipe(
            retry({
                count: Math.max(0, attempts - 1),       // e.g., 3 attempts total
                delay: (error, retryCount) => {
                    this.logger.warn(`Request timed out or failed. Retrying attempt ${retryCount + 1} of ${attempts}.`, error);
                    return timer(delayMs)   // fixed delay between attempts
                }
            })
        );
    }

}
